import { Injectable } from '@angular/core';
import { ProcessorType } from '@portal/entity-services/interfaces';
import { Features } from 'environments/enums/features.enum';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle';
import { SelectInputsList } from '@portal/shared/helpers/src';
import { ISelectInput } from '@portal/shared/ui/filter';
import sortBy from 'lodash-es/sortBy';
import { SupportedProcessorSettingsService } from '@apps/portal/src/app/services/supported-processor-settings.service';
import { TranslationsKeyValue } from '@portal/shared/helpers/src/lib/create-list-from-inputs/interfaces/translations-key-value.interface';

declare const $localize;

@Injectable({ providedIn: 'root' })
export class ProcessorTypes extends SelectInputsList {
  keyValue: TranslationsKeyValue = {
    [ProcessorType.AbsIso]: $localize`:Processor type|:ABS ISO`,
    [ProcessorType.AbsWeb]: $localize`:Processor type|:ABS Web`,
    [ProcessorType.Affirm]: $localize`:Processor type|:Affirm`,
    [ProcessorType.AliPay]: $localize`:Processor type|:Alipay`,
    [ProcessorType.AmexGB]: $localize`:Processor type|:American Express`,
    [ProcessorType.AmexMX]: $localize`:Processor type|:Amex Mexico`,
    [ProcessorType.AmexPWP]: $localize`:Processor type|:Amex Pay With Points`,
    [ProcessorType.Bankaxept]: $localize`:Processor type|:BankAxept`,
    [ProcessorType.Banorte]: $localize`:Processor type|:Banorte`,
    [ProcessorType.BanqueChabrieres]: $localize`:Processor type|:Banque Chabrières`,
    [ProcessorType.Barclays]: $localize`:Processor type|:Barclays`,
    [ProcessorType.BarclaysMc]: $localize`:Processor type|:Barclays Multicurrency`,
    [ProcessorType.BbvaMx]: $localize`:Processor type|:BBVA Mexico`,
    [ProcessorType.Bfcoi]: $localize`:Processor type|:Banque Française Commerciale Océan Indien`,
    [ProcessorType.Blik]: $localize`:Processor type|:Blik`,
    [ProcessorType.BlackhawkCa]: $localize`:Processor type|:Blackhawk CA`,
    [ProcessorType.BlackhawkUs]: $localize`:Processor type|:Blackhawk US`,
    [ProcessorType.BnpParibas]: $localize`:Processor type|:BNPP`,
    [ProcessorType.BnppVersAtos]: $localize`:Processor type|:BNPP ATC`,
    [ProcessorType.BofAUs]: $localize`:Processor type|:BofA US`,
    [ProcessorType.Bred]: $localize`:Processor type|:BRED`,
    [ProcessorType.CaisseDepargne]: $localize`:Processor type|:Caisse d'Epargne`,
    [ProcessorType.Cba]: $localize`:Processor type|:CBA`,
    [ProcessorType.Cedicam]: $localize`:Processor type|:CEDICAM`,
    [ProcessorType.ChaseCa]: $localize`:Processor type|:Chase CA`,
    [ProcessorType.ChaseUs]: $localize`:Processor type|:Chase US`,
    [ProcessorType.Cic]: $localize`:Processor type|:Crédit Industriel et Commercial (CIC)`,
    [ProcessorType.CitibankCa]: $localize`:Processor type|:Citibank CA`,
    [ProcessorType.CitibankUs]: $localize`:Processor type|:Citibank US`,
    [ProcessorType.CmArkea]: $localize`:Processor type|:Crédit Mutuel Arkéa`,
    [ProcessorType.CreditAgricole]: $localize`:Processor type|:Crédit Agricole`,
    [ProcessorType.CreditDuNordVersAtos]: $localize`:Processor type|:Crédit du Nord to ATOS`,
    [ProcessorType.CreditDuNordVersNatixis]: $localize`:Processor type|:Crédit du Nord vers Natixis`,
    [ProcessorType.CreditMutuel]: $localize`:Processor type|:Crédit Mutuel`,
    [ProcessorType.Credomatic]: $localize`:Processor type|:CREDOMATIC`,
    [ProcessorType.Crypto]: $localize`:Processor type|:Crypto`,
    [ProcessorType.CuscalAu]: $localize`:Processor type|:Cuscal AU`,
    [ProcessorType.Cybersource]: $localize`:Processor type|:Cybersource`,
    [ProcessorType.Dankort]: $localize`:Processor type|:Dankort`,
    [ProcessorType.EftposNz]: $localize`:Processor type|:EFTPOS NZ`,
    [ProcessorType.Elavon]: $localize`:Processor type|:Elavon`,
    [ProcessorType.ElavonIsoUk]: $localize`:Processor type|:Elavon ISO UK`,
    [ProcessorType.Fdrc]: $localize`:Processor type|:First Data Rapid Connect North America`,
    [ProcessorType.FdrcCa]: $localize`:Processor type|:Fiserv (First Data) CA`,
    [ProcessorType.FdrcUs]: $localize`:Processor type|:Fiserv (First Data) US`,
    [ProcessorType.FdrcPetroUs]: $localize`:Processor type|:Fiserv (First Data) Petro US`,
    [ProcessorType.FiservAu]: $localize`:Processor type|:Fiserv AU`,
    [ProcessorType.FiservLac]: $localize`:Processor type|:Fiserv LAC`,
    [ProcessorType.FiservUk]: $localize`:Processor type|:Fiserv UK`,
    [ProcessorType.Gicc]: $localize`:Processor type|:GICC`,
    [ProcessorType.Globalpay]: $localize`:Processor type|:GlobalPay`,
    [ProcessorType.GPP2]: $localize`:Processor type|:GPP2`,
    [ProcessorType.Hsbc]: $localize`:Processor type|:HSBC`,
    [ProcessorType.Ikano]: $localize`:Processor type|:Ikano`,
    [ProcessorType.IncommCa]: $localize`:Processor type|:Incomm CA`,
    [ProcessorType.IncommUs]: $localize`:Processor type|:Incomm US`,
    [ProcessorType.Intercard]: $localize`:Processor type|:Verifone Payments GmbH`,
    [ProcessorType.Isracard]: $localize`:Processor type|:Isracard`,
    [ProcessorType.KlarnaEcom]: $localize`:Processor type|:Klarna Ecom`,
    [ProcessorType.KlarnaQr]: $localize`:Processor type|:Klarna Pos`,
    [ProcessorType.LaBanquePostal]: $localize`:Processor type|:La Banque Postale`,
    [ProcessorType.Landsbankinn]: $localize`:Processor type|:Landsbankinn`,
    [ProcessorType.Lcl]: $localize`:Processor type|:Crédit Lyonnais (LCL)`,
    [ProcessorType.LesBanquesPopulaires]: $localize`:Processor type|:Les Banques Populaires (NATIXIS)`,
    [ProcessorType.LloydsCardnet]: $localize`:Processor type|:Lloyds Bank`,
    [ProcessorType.Maestro]: $localize`:Processor type|:Maestro`,
    [ProcessorType.Mobilepay]: $localize`:Processor type|:MobilePay`,
    [ProcessorType.MPGS]: $localize`:Processor type|:Mastercard Payment Gateway Services`,
    [ProcessorType.Nets]: $localize`:Processor type|:Nets`,
    [ProcessorType.Omnipay]: $localize`:Processor type|:Omnipay`,
    [ProcessorType.OpOnlinePayment]: $localize`:Processor type|:OpOnlinePayment`,
    [ProcessorType.ParkRetailUk]: $localize`:Processor type|:Park Retail`,
    [ProcessorType.Paypal]: $localize`:Processor type|:PayPal`,
    [ProcessorType.PaypalBraintree]: $localize`:Processor type|:PayPal Braintree`,
    [ProcessorType.PaypalUk]: $localize`:Processor type|:PayPal UK`,
    [ProcessorType.Prm]: $localize`:Processor type|:PRM`,
    [ProcessorType.Prosa]: $localize`:Processor type|:Prosa`,
    [ProcessorType.Rapyd]: $localize`:Processor type|:Rapyd`,
    [ProcessorType.ResursBank]: $localize`:Processor type|:Resurs Bank`,
    [ProcessorType.Sg]: $localize`:Processor type|:Société Générale`,
    [ProcessorType.Simulator]: $localize`:Processor type|:Simulator`,
    [ProcessorType.SvsUk]: $localize`:Processor type|:SVS UK`,
    [ProcessorType.SvsUs]: $localize`:Processor type|:SVS US`,
    [ProcessorType.SwedbankPay]: $localize`:Processor type|:Swedbank`,
    [ProcessorType.Swish]: $localize`:Processor type|:Swish`,
    [ProcessorType.SynchronyUs]: $localize`:Processor type|:Synchrony`,
    [ProcessorType.TelecheckUS]: $localize`:Processor type|:Telecheck US`,
    [ProcessorType.TeyaEu]: $localize`:Processor type|:TEYA EU`,
    [ProcessorType.TietoEvry]: $localize`:Processor type|:TietoEVRY`,
    [ProcessorType.UnionPay]: $localize`:Processor type|:UnionPay`,
    [ProcessorType.ValuelinkCA]: $localize`:Processor type|:Valuelink CA`,
    [ProcessorType.ValuelinkUS]: $localize`:Processor type|:Valuelink US`,
    [ProcessorType.VerifoneCmsEu]: $localize`:Processor type|:Verifone Europe via CMS`,
    [ProcessorType.VerifoneEu]: $localize`:Processor type|:Verifone Europe`,
    [ProcessorType.Vipps]: $localize`:Processor type|:Vipps`,
    [ProcessorType.WeChat]: $localize`:Processor type|:Wechat`,
    [ProcessorType.Westpac]: $localize`:Processor type|:Westpac`,
    [ProcessorType.WestpacAu]: $localize`:Processor type|:Verifone Australia Gateway`,
    [ProcessorType.WestpacPacificFiji]: $localize`:Processor type|:Westpac Pacific FIJI`,
    [ProcessorType.WestpacPacificPng]: $localize`:Processor type|:Westpac Pacific PNG`,
    [ProcessorType.WexUs]: $localize`:Processor type|:WEX (US)`,
    [ProcessorType.WicUsTx]: $localize`:Processor type|:WIC Texas`,
    [ProcessorType.WoodForestUs]: $localize`:Processor type|:Woodforest National Bank`,
    [ProcessorType.WorldlineUK]: $localize`:Processor type|:Worldline UK`,
    [ProcessorType.Worldpay]: $localize`:Processor type|:WorldPay`,
    [ProcessorType.WorldpayUS]: $localize`:Processor type|:WorldPay US`,
    [ProcessorType.UnifyGivex]: $localize`:Processor type|:GIVEX`,
    [ProcessorType.Wpay]: $localize`:Processor type|:WPAY`,
  };

  list: ISelectInput[];

  constructor(
    private featureToggleService?: FeatureToggle,
    private supportedProcessorSettingsService?: SupportedProcessorSettingsService,
  ) {
    super();
    if (!this.featureToggleService?.isFeatureActive(Features.MobilePay)) {
      delete this.keyValue[ProcessorType.Mobilepay];
    }
    if (!this.featureToggleService?.isFeatureActive(Features.Simulator)) {
      delete this.keyValue[ProcessorType.Simulator];
    }
    if (!this.featureToggleService?.isFeatureActive(Features.Crypto)) {
      delete this.keyValue[ProcessorType.Crypto];
    }
    if (!this.featureToggleService?.isConfigFeatureActive(Features.EnableOmnipayProcessor)) {
      delete this.keyValue[ProcessorType.Omnipay];
    }
    this.getSortedList();
  }

  getSupportedProcessors(): ISelectInput[] {
    // Use the new getProcessorsSupported method that handles destinationProcessorsSupported
    const processors = this.supportedProcessorSettingsService?.getProcessorsSupported();
    if (processors?.length) {
      const supportedProcessors: TranslationsKeyValue = {};
      processors.forEach((processor) => {
        supportedProcessors[processor] = this.keyValue[processor] || processor;
      });
      this.getSortedList(supportedProcessors);
    }
    return this.list;
  }

  /**
   * Get processor configuration including selector and list
   * Returns object with selector (destinationProcessor or sourcePIM) and processors list
   * Pass includeDestinationProcessors = false to ignore destinationProcessorsSupported
   */
  getProcessorConfiguration(includeDestinationProcessors = true): {
    selector: string;
    processors: ISelectInput[];
  } {
    const settings =
      this.supportedProcessorSettingsService?.getSupportedProcessorSettingsFromSessionStorage();
    const destinationProcessors = includeDestinationProcessors
      ? settings?.destinationProcessorsSupported
      : undefined;
    const sourceProcessors = settings?.processorsSupported;

    // Determine selector and processors to use
    let selector = 'sourcePIM';
    let processorsToUse = sourceProcessors;

    // Prioritize destinationProcessors if available
    if (destinationProcessors?.length > 0) {
      selector = 'destinationProcessor';
      processorsToUse = destinationProcessors;
    }

    // Build processor list based on count
    if (processorsToUse?.length > 0) {
      // If processors are configured, return only those processors
      const supportedProcessors: TranslationsKeyValue = {};
      processorsToUse.forEach((processor) => {
        supportedProcessors[processor] = this.keyValue[processor] || processor;
      });
      this.getSortedList(supportedProcessors);
    } else {
      // No processors configured (0 or undefined) - use full enum list
      this.getSortedList();
    }

    return {
      selector,
      processors: this.list,
    };
  }

  getSortedList(processors = this.keyValue): void {
    this.list = sortBy(this.getSelectInputs(processors), (item: ISelectInput) =>
      item.text.toLowerCase(),
    );
  }
}
